/**
 * Story 054 — fresh `/me` roles for in-modal role gating.
 *
 * `useMe` from `@aeriosiq/auth-client` short-circuits with `session.user` as
 * initialData + `staleTime: 60_000`, so it never refetches and never
 * sees updated `/me` payloads. RequireRole (the route guard) works
 * around this by issuing its own `/me` fetch — same approach here for
 * the EditSiteDialog's mutate-role gate. The fetch fires once on
 * mount; while pending we render with the optimistic session roles.
 */
import { useEffect, useState } from "react";
import { getAuthBaseUrl, useAuthStore } from "@aeriosiq/auth-client";

interface MeResponse {
  roles?: readonly string[];
}

export function useFreshRoles(): readonly string[] {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const accessToken = session?.accessToken;
  const tenantSlug = tenant?.tenantSlug;
  const seedRoles: readonly string[] = session?.user.roles ?? [];

  const [roles, setRoles] = useState<readonly string[]>(seedRoles);

  useEffect(() => {
    if (!accessToken || !tenantSlug) return;
    let cancelled = false;
    fetch(`${getAuthBaseUrl()}/me`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "X-Tenant-ID": tenantSlug,
      },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`/me ${String(res.status)}`);
        return (await res.json()) as MeResponse;
      })
      .then((me) => {
        if (cancelled) return;
        if (me.roles) setRoles(me.roles);
      })
      .catch(() => {
        // Keep the seed roles on network failure.
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, tenantSlug]);

  return roles;
}
